import React, { useEffect, useRef, useState, useCallback } from "react";
import { Route, Switch } from "react-router";
import { BrowserRouter } from "react-router-dom";
import {
  Login,
  Dashboard,
  NoticeListPage,
  NoticeDetailPage,
  Order,
  InhouseNotice,
  InhouseNoticeDetail,
  As,
  AsRegister,
  Settings,
  Part,
  PartRegister,
  Tds,
  TdsRegister,
  Consignment,
  Purchase,
  Consult,
  ConsultRegister,
  ConsultImsiList,
  Appraisal,
  AppraisalDetail,
  AppraisalRegister,
  Schedule,
  Account,
  PurchaseEdit,
  ConsignmentEdit,
  User,
  UserRegister,
  Check,
  ParcelPurchase,
} from "../pages";
import { SWRConfig } from "swr";
import AlertPopup from "../component/popup/AlertPopup";
import useLoad from "../hooks/useLoad";
import swrConfig from "../swrConfig";
import withUser from "../hoc/withUser";
import WithAccessAuth from "../hoc/WithAccessAuth";
import Core from "../container/base/Core";
import auth from "../utils/auth";
import LogoutTimer from "../component/LogoutTimer";
import { runLogoutDraftSaver } from "../utils/logoutDraftSaver";

// setDateTime 함수를 컴포넌트 외부로 이동
function setDateTime(item, type, unit) {
  let setDate = "";
  if (item) {
    let basicDate = String(item).indexOf("-") > 0 || String(item).indexOf(".") > 0 
      ? String(item).replace(/[-.]/g, "/") 
      : item;
    let date = new Date(basicDate),
      yy,
      mm,
      dd,
      th,
      tm,
      ts;

    yy = date.getFullYear();
    mm = date.getMonth() + 1 <= 9 ? "0" + (date.getMonth() + 1) : date.getMonth() + 1;
    dd = date.getDate() <= 9 ? "0" + date.getDate() : date.getDate();
    th = date.getHours() <= 9 ? "0" + date.getHours() : date.getHours();
    tm = date.getMinutes() <= 9 ? "0" + date.getMinutes() : date.getMinutes();
    ts = date.getSeconds() <= 9 ? "0" + date.getSeconds() : date.getSeconds();

    if (type === "yymmdd") {
      if (unit === "ymd") {
        setDate = yy + "년 " + mm + "월 " + dd + "일";
      } else if (unit === "hymd") {
        setDate = yy + "-" + mm + "-" + dd;
      } else if (unit === "not") {
        setDate = `${yy}${mm}${dd}`;
      } else {
        setDate = yy + "." + mm + "." + dd;
      }
    } else if (type === "yymmddhhtt") {
      if (unit === "ymd") {
        setDate = yy + "년 " + mm + "월 " + dd + "일 " + th + ":" + tm;
      } else if (unit === "hymd") {
        setDate = yy + "-" + mm + "-" + dd + " " + th + ":" + tm;
      } else {
        setDate = yy + "." + mm + "." + dd + " " + th + ":" + tm;
      }
    } else if (type === "mmdd") {
      if (unit === "ymd") {
        setDate = mm + "월 " + dd + "일";
      } else if (unit === "hymd") {
        setDate = mm + "-" + dd;
      } else if (unit === "slash") {
        setDate = mm + "/" + dd;
      } else {
        setDate = mm + "." + dd;
      }
    } else if (type === "yymm") {
      if (unit === "ymd") {
        setDate = yy + "년 " + mm + "월";
      } else if (unit === "hymd") {
        setDate = yy + "-" + mm;
      } else if (unit === "slash") {
        setDate = yy + "/" + mm;
      } else {
        setDate = yy + "." + mm;
      }
    } else if (type === "day") {
      setDate = dd;
    } else if (type === "yymmddhhttss") {
      if (unit === "ymd") {
        setDate = yy + "년 " + mm + "월 " + dd + "일 " + th + ":" + tm + ":" + ts;
      } else if (unit === "hymd") {
        setDate = yy + "-" + mm + "-" + dd + " " + th + ":" + tm + ":" + ts;
      } else {
        setDate = yy + "." + mm + "." + dd + " " + th + ":" + tm + ":" + ts;
      }
    } else if (type === "hhttss") {
      setDate = th + ":" + tm + ":" + ts;
    } else if (type === "hhtt") {
      setDate = th + ":" + tm;
    } else {
      setDate = yy + "." + mm + "." + dd + " " + th + ":" + tm + ":" + ts;
    }
  }
  return setDate;
}

const App = () => {
  const [alertPopup, setAlertPopup] = useState(null);
  const [logoutTime, setLogoutTime] = useState(null);
  const [logoutTimeStored, setLogoutTimeStored] = useState(null);

  const lastActivityTime = useRef(Date.now());
  const lastServerSync = useRef(Date.now());
  const isLoggingOut = useRef(false);
  const activityListenerRef = useRef(null);

  const failAlertPopup = ({ state, title, text }) => {
    setAlertPopup({
      state,
      title,
      text,
    });
  };

  useLoad();

  const handleForceLogout = useCallback(async () => {
    // 재진입 가드: 신규 부작용(imsi 임시저장)이 두 번 실행돼 초안이 중복 생성되는 것을 방지
    if (isLoggingOut.current) return;
    isLoggingOut.current = true;

    sessionStorage.setItem('isAutoLogout', 'true');

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      if (activityListenerRef.current) {
        window.removeEventListener(event, activityListenerRef.current);
      }
    });

    // 작성 중인 상담일지가 있으면 토큰/스토리지를 지우기 전에 임시저장.
    // (등록된 saver 가 없으면 즉시 통과, 실패해도 아래 로그아웃은 계속 진행)
    await runLogoutDraftSaver();

    localStorage.removeItem("logoutTime");
    localStorage.removeItem("logoutTimeMinutes");
    auth.clearAppStorage();
    localStorage.setItem("logoutAll", true);

    window.location.href = "/login";
    window.location.reload(true);
  }, []);

  const syncServerSession = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) return false;

    console.log('[Sync] 서버 동기화 시작...');

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_END_POINT}/manager/session/refresh`,
        {
          method: "POST",
          headers: {
            'Authorization': 'Bearer ' + token,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            activity_time: new Date().toISOString()
          })
        }
      );

      console.log('[Sync] 응답 상태:', response.status);

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          console.error('[Sync] ❌ 토큰 만료 - 로그아웃');
          handleForceLogout();
          return false;
        }
        throw new Error(`Session sync failed: ${response.status}`);
      }

      const data = await response.json();
      console.log('[Sync] ✅ 성공:', data);
      
      lastServerSync.current = Date.now();

      return data.success === true;
    } catch (error) {
      console.error('[Sync] 에러:', error);
      return false;
    }
  }, [handleForceLogout]);

  const updateLogoutTime = useCallback(async (minutes, forceServerSync = false) => {
    const mins = minutes || logoutTime;
    if (!mins) return;

    let nowTime = new Date();
    nowTime.setMinutes(nowTime.getMinutes() + mins);
    const timeStr = setDateTime(nowTime, "yymmddhhttss", "hymd");

    localStorage.setItem("logoutTime", timeStr);
    setLogoutTimeStored(timeStr);

    console.log('[Update] forceServerSync:', forceServerSync);

    if (forceServerSync) {
      console.log('[Update] 서버 동기화 시작...');
      const success = await syncServerSession();
      if (success) {
        console.log('[Update] ✅ 서버 동기화 성공');
      } else {
        console.log('[Update] ⚠️ 서버 동기화 실패');
      }
    }
  }, [logoutTime, syncServerSession]);

  const handleUserActivity = useCallback(() => {
    const now = Date.now();

    if (now - lastActivityTime.current < 1000) {
      return;
    }

    if (isLoggingOut.current) {
      return;
    }

    lastActivityTime.current = now;

    if (localStorage.getItem("token") && logoutTime) {
      const timeSinceLastSync = now - lastServerSync.current;
      const syncInterval = 5 * 60 * 1000;
      
      if (timeSinceLastSync >= syncInterval) {
        console.log('[Activity] 5분 경과 - 서버 동기화');
        updateLogoutTime(logoutTime, true);
      } else {
        updateLogoutTime(logoutTime, false);
      }
    }
  }, [logoutTime, updateLogoutTime]);

  function logoutTimeSetting() {
    const token = localStorage.getItem("token");
    const storedLogoutTime = localStorage.getItem("logoutTimeMinutes");

    if (!token) {
      localStorage.removeItem("logoutTime");
      localStorage.removeItem("logoutTimeMinutes");
      return;
    }

    // ⭐ 이미 로그인 시 저장되어 있으면 그것 사용
    if (storedLogoutTime) {
      const minutes = Number(storedLogoutTime);
      setLogoutTime(minutes);

      let nowTime = new Date();
      nowTime.setMinutes(nowTime.getMinutes() + minutes);
      const timeStr = setDateTime(nowTime, "yymmddhhttss", "hymd");
      localStorage.setItem("logoutTime", timeStr);
      setLogoutTimeStored(timeStr);
      return;
    }

    // ⚠️ 백업: 저장된 값이 없으면 API 호출 (혹시 모를 상황 대비)
    const headers = {
      'Accept': 'application/json',
      'Authorization': 'Bearer ' + token
    };

    fetch(`${process.env.REACT_APP_API_END_POINT}/manager/setting/logout/get`, {
      method: "GET",
      headers,
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error("logoutTimeSetting request failed: " + response.status);
        }
        return response.json();
      })
      .then(function (fetchRd) {
        if (fetchRd.success === true) {
          const minutes = Number(fetchRd?.data?.logout_time);
          setLogoutTime(minutes);
          localStorage.setItem("logoutTimeMinutes", minutes.toString());

          if (minutes > 0) {
            let nowTime = new Date();
            nowTime.setMinutes(nowTime.getMinutes() + minutes);
            const timeStr = setDateTime(nowTime, "yymmddhhttss", "hymd");
            localStorage.setItem("logoutTime", timeStr);
            setLogoutTimeStored(timeStr);
          }
        }
      })
      .catch(error => {
        console.error('Failed to fetch logout setting:', error);
        const defaultMinutes = 20;
        setLogoutTime(defaultMinutes);
        localStorage.setItem("logoutTimeMinutes", defaultMinutes.toString());
        
        let nowTime = new Date();
        nowTime.setMinutes(nowTime.getMinutes() + defaultMinutes);
        const timeStr = setDateTime(nowTime, "yymmddhhttss", "hymd");
        localStorage.setItem("logoutTime", timeStr);
        setLogoutTimeStored(timeStr);
      });
  }

  const handleExtendTime = useCallback(async () => {
    console.log('=== [EXTEND TIME CLICKED] ===');
    
    if (!logoutTime) return;

    lastActivityTime.current = Date.now();

    let nowTime = new Date();
    nowTime.setMinutes(nowTime.getMinutes() + logoutTime);
    const timeStr = setDateTime(nowTime, "yymmddhhttss", "hymd");

    localStorage.setItem("logoutTime", timeStr);
    setLogoutTimeStored(timeStr);
    
    console.log('[Extend Time] 로컬 시간 연장 완료');
    console.log('[Extend Time] 서버 토큰 갱신 시도...');
    
    try {
      const success = await syncServerSession();
      if (success) {
        console.log('[Extend Time] ✅ 토큰 갱신 성공');
      } else {
        console.log('[Extend Time] ⚠️ 토큰 갱신 실패');
      }
    } catch (error) {
      console.error('[Extend Time] 에러:', error);
    }
  }, [logoutTime]); //syncServerSession

  useEffect(() => {
    const storedMinutes = localStorage.getItem("logoutTimeMinutes");
    if (storedMinutes && !logoutTime) {
      setLogoutTime(Number(storedMinutes));
    }
  }, [logoutTime]);

  useEffect(() => {
    let nowTime = new Date();
    if (logoutTime || logoutTime === 0) {
      nowTime.setMinutes(nowTime.getMinutes() + Number(logoutTime));
      const timeStr = setDateTime(nowTime, "yymmddhhttss", "hymd");
      localStorage.setItem("logoutTime", timeStr);
      setLogoutTimeStored(timeStr);
    }
  }, [logoutTime]);

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      localStorage.removeItem("logoutTime");
      return;
    }

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    activityListenerRef.current = handleUserActivity;

    events.forEach(event => {
      window.addEventListener(event, handleUserActivity, { passive: true });
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleUserActivity);
      });
    };
  }, [handleUserActivity]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const logoutTimeStr = localStorage.getItem("logoutTime");

    if (!token) {
      localStorage.removeItem("logoutTime");
      return;
    }

    if (logoutTimeStr) {
      const chkTime = new Date();
      let logoutTimeSet = String(logoutTimeStr).indexOf("-") > 0 || 
                        String(logoutTimeStr).indexOf(".") > 0 
        ? String(logoutTimeStr).replace(/[-.]/g, "/") 
        : logoutTimeStr;
      const logoutTime = new Date(logoutTimeSet);
      
      if (chkTime >= logoutTime) {
        handleForceLogout();
        return;
      }
      
      setLogoutTimeStored(logoutTimeStr);
    }

    if (token && !logoutTimeStr) {
      logoutTimeSetting();
    }
  }, [handleForceLogout]);

  useEffect(() => {
    const handleStorageChange = () => {
      if (!localStorage.getItem("token") && localStorage.getItem("logoutAll")) {
        localStorage.removeItem("logoutAll");
        window.location.href = "/login";
        window.location.reload(true);
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      const token = localStorage.getItem("token");
      if (token) {
        navigator.sendBeacon(
          `${process.env.REACT_APP_API_END_POINT}/manager/session/refresh`,
          JSON.stringify({
            activity_time: new Date().toISOString()
          })
        );
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const logoutTimeMinutes = logoutTime || 
    (localStorage.getItem("logoutTimeMinutes") ? Number(localStorage.getItem("logoutTimeMinutes")) : 30);

  return (
    <SWRConfig value={swrConfig(failAlertPopup)}>
      <BrowserRouter>
        {logoutTimeStored && (
          <LogoutTimer 
            logoutTimeStored={logoutTimeStored}
            onForceLogout={handleForceLogout}
            onExtendTime={handleExtendTime}
            logoutTimeMinutes={logoutTimeMinutes}
            lastActivityTime={lastActivityTime.current}
          />
        )}

        <Switch>
          <Route exact path="/" component={Dashboard} />
          <Route exact path="/login" component={Login} />
          <Route exact path="/check" component={Check} />
          <Route exact path="/dashboard" component={Dashboard} />
          <Route exact path="/dashboard/notice" component={NoticeListPage} />
          <Route exact path="/dashboard/notice/detail/:detailId" component={NoticeDetailPage} />
          <Route exact path="/inhousenotice" component={InhouseNotice}>
            <WithAccessAuth Component={InhouseNotice} level={2} redirectUrl="/" />
          </Route>
          <Route exact path="/inhousenotice/detail/:detailId" component={InhouseNoticeDetail} />
          <Route exact path="/as">
            <WithAccessAuth Component={As} level={2} redirectUrl="/" />
          </Route>
          <Route exact path="/parcel-purchase">
            {/* 8.22 몰어드민에서 이관 — level3+. 관리지점 계정 게이트는 서버(branchAllowed)+사이드메뉴에서 처리 */}
            <WithAccessAuth Component={ParcelPurchase} level={3} redirectUrl="/" />
          </Route>
          <Route exact path="/as/register" component={AsRegister} />
          <Route exact path="/as/edit/:detailId" component={AsRegister} />
          <Route exact path="/part" component={Part} />
          <Route exact path="/part/edit/:detailId" component={PartRegister} />
          <Route exact path="/part/register" component={PartRegister} />
          <Route exact path="/consult">
            <WithAccessAuth Component={withUser(Consult)} level={3} redirectUrl="/" />
          </Route>
          <Route exact path="/consult/edit/:detailId" component={ConsultRegister} />
          <Route exact path="/consult/register" component={ConsultRegister} />
          <Route exact path="/consult/imsi" component={ConsultImsiList} />
          <Route exact path="/appraisal">
            <WithAccessAuth Component={withUser(Appraisal)} level={2} redirectUrl="/" />
          </Route>
          <Route exact path="/appraisal/register">
            <WithAccessAuth Component={withUser(AppraisalRegister)} level={2} redirectUrl="/appraisal" />
          </Route>
          <Route exact path="/appraisal/detail/:detailId">
            <WithAccessAuth Component={withUser(AppraisalDetail)} level={2} redirectUrl="/appraisal" />
          </Route>
          <Route exact path="/tds">
            <WithAccessAuth Component={Tds} level={3} redirectUrl="/" />
          </Route>
          <Route exact path="/tds/edit/:detailId" component={TdsRegister} />
          <Route exact path="/tds/register" component={TdsRegister} />
          <Route exact path="/consignment">
            <WithAccessAuth Component={withUser(Consignment)} level={3} redirectUrl="/" />
          </Route>
          <Route exact path="/consignment/edit/:detailId">
            <WithAccessAuth Component={withUser(ConsignmentEdit)} level={3} redirectUrl="/consignment" />
          </Route>
          <Route exact path="/account">
            <WithAccessAuth Component={withUser(Account)} level={4} redirectUrl="/consignment" />
          </Route>
          <Route exact path="/schedule" component={Schedule} />
          <Route exact path="/purchase">
            <WithAccessAuth Component={withUser(Purchase)} level={3} redirectUrl="/" />
          </Route>
          <Route exact path="/purchase/edit/:detailId">
            <WithAccessAuth Component={withUser(PurchaseEdit)} level={3} redirectUrl={"/purchase"} />
          </Route>
          <Route exact path="/settings" component={Settings} />
          <Route exact path="/order" component={Order} />
          <Route exact path="/user" component={User} />
          <Route exact path="/user/register" component={UserRegister} />
          <Route exact path="/user/edit/:detailId" component={UserRegister} />
        </Switch>
      </BrowserRouter>

      {alertPopup !== null && (
        <AlertPopup
          handleClose={() => (window.location.href = "/dashboard")}
          firstBtnClick={() => (window.location.href = "/dashboard")}
          title={alertPopup.title}
          text={alertPopup.text}
        />
      )}

      <Core />
    </SWRConfig>
  );
};

export default App;